/**
 * Barra in alto nella lista messaggi con il messaggio fissato e "Sblocca".
 * Clic sul testo: porta al messaggio (se e' tra quelli caricati).
 */

import { memo, useState } from 'react';
import { Pin, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { tip } from '@/components/messages/ChatTooltip';
import { useMessageMarksStore } from './messageMarksStore';
import { messagePreviewText } from './messageExtras';
import type { Message } from '@/lib/types/whatsappChat';

interface PinnedBarProps {
  messages: Message[];
  canUnpin: boolean;
  /** Chiave (numero|chat) della chat aperta: i segni di un'altra chat non si mostrano. */
  marksKey?: string | null;
}

export const PinnedBar = memo(function PinnedBar({ messages, canUnpin, marksKey }: PinnedBarProps) {
  const pinnedId = useMessageMarksStore((s) => (marksKey === undefined || s.key === marksKey ? s.marks.pinnedId : null));
  const unpin = useMessageMarksStore((s) => s.unpin);
  const [busy, setBusy] = useState(false);
  if (!pinnedId) return null;

  const msg = messages.find((m) => m.id === pinnedId);
  const preview = msg ? messagePreviewText(msg.type, msg.content) : 'Messaggio fissato (non tra gli ultimi caricati)';

  const jump = () => {
    const el = document.getElementById(`msg-${pinnedId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else toast('Il messaggio fissato e\' piu\' indietro: scorri verso l\'alto per caricarlo');
  };

  const handleUnpin = async () => {
    setBusy(true);
    try {
      await unpin();
    } catch (err) {
      console.error('[PinnedBar] unpin error:', err);
      toast.error('Non sono riuscito a sbloccare il messaggio. Riprova tra poco.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sticky top-0 z-10 -mx-4 mb-2 flex items-center gap-2 px-4 py-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
      <Pin className="w-4 h-4 text-[#2383E2] flex-shrink-0" aria-hidden />
      <button type="button" onClick={jump} className="flex-1 min-w-0 text-left" {...tip('Vai al messaggio fissato')}>
        <p className="text-[11px] font-semibold text-[#2383E2]">Messaggio fissato</p>
        <p className="text-xs text-[#37352F] dark:text-white/90 truncate">{preview}</p>
      </button>
      {canUnpin && (
        <button
          type="button"
          onClick={handleUnpin}
          disabled={busy}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 disabled:opacity-50"
          {...tip('Togli il messaggio dalla barra in alto, anche sul telefono')}
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          Sblocca
        </button>
      )}
    </div>
  );
});
